<script setup>
import {
  computed, inject, nextTick, ref, watch,
} from 'vue';
import seleccionDestinatariosApi from '@/api/cifra/seleccion_destinatarios';
import { useSeleccionMultiple } from '@/composables/useSeleccionMultiple';
import { fullname } from '@/libs/usuarios';
import ModalSimple from '../ModalSimple.vue';
import SelectorEstructura from '../SelectorEstructura.vue';
import { confirmar } from '../../libs/cifra/dialogs';
import InputCheckboxBoolean from '../cifra/InputCheckboxBoolean.vue';

const I18n = inject('I18n');

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
const emits = defineEmits(['update:modelValue', 'destinatarios-anadidos']);

const TIPOS_CON_ESTRUCTURA = ['alumno', 'profesor', 'asignatura'];
const TIPOS_CON_TIPOS_DESTINATARIO = ['alumno', 'profesor', 'asignatura'];

const tiposBusqueda = ref([]);
const tipo = ref(null);
const filtroNombre = ref('');
const filtroRolIds = ref([]);
const filtroGdcIds = ref([]);
const filtroDireccionIds = ref([]);
const roles = ref([]);
const gdcs = ref([]);
const direcciones = ref([]);
const resultados = ref([]);
const seleccionados = ref([]);
const tiposDestinatarioOpciones = ref([]);
const tiposDestinatarioSeleccionados = ref([]);
const inicializando = ref(false);
const buscando = ref(false);
const enviando = ref(false);
const errorBusqueda = ref(null);
const selectorEstructura = ref(null);

const esMiembroCentro = computed(() => tiposBusqueda.value.some((t) => t.tipo === 'alumno'),
);

const mostrarTipoSeleccion = computed(() => tiposBusqueda.value.length > 1);

const usaEstructura = computed(() => TIPOS_CON_ESTRUCTURA.includes(tipo.value));

const usaFiltroNombre = computed(() => ['alumno', 'profesor', 'otro'].includes(tipo.value),
);

const usaRoles = computed(() => tipo.value === 'otro');

const usaGdc = computed(() => tipo.value === 'gdc');

const usaDirecciones = computed(() => tipo.value === 'mensajeria_direccion');

const mostrarTiposDestinatario = computed(
  () => TIPOS_CON_TIPOS_DESTINATARIO.includes(tipo.value)
    && tiposDestinatarioOpciones.value.length > 0,
);

const TIPOS_SELECCIONAR_TODOS = [
  'gdc',
  'otro',
  'asignatura',
  'alumno',
  'profesor',
];

const idsSeleccionables = computed(() => {
  if (usaDirecciones.value) {
    return filtroDireccionIds.value;
  }
  return resultados.value.map((r) => r.id);
});

const idsGdcsSeleccionables = computed(() => gdcs.value.map((gdc) => gdc.id));

const tiposDestinatarioValores = computed(
  () => tiposDestinatarioOpciones.value.map((t) => t.tipo),
);

const seleccionRol = useSeleccionMultiple(filtroRolIds, computed(() => []));
const seleccionDireccion = useSeleccionMultiple(filtroDireccionIds, computed(() => []));
const seleccionResultados = useSeleccionMultiple(seleccionados, idsSeleccionables);
const seleccionGdc = useSeleccionMultiple(filtroGdcIds, idsGdcsSeleccionables);
const seleccionTipoDestinatario = useSeleccionMultiple(
  tiposDestinatarioSeleccionados, tiposDestinatarioValores,
);

const seleccionarTodosEnBusqueda = computed(() => TIPOS_SELECCIONAR_TODOS.includes(tipo.value),
);

const tituloPanelSeleccion = computed(
  () => errorBusqueda.value
    || I18n.t('seleccionar_destinatarios_cantidad', {
      cantidad: resultados.value.length,
    }),
);

function nombreResultado(item) {
  if (item.apellido1 || item.apellido2) {
    return fullname(item);
  }
  return item.nombre || '';
}

function filtroEstructuraActual() {
  const hash = selectorEstructura.value?.getPathSeleccionadoEnHash({ soloTiposPermitidos: true });
  if (!hash) return {};
  const filtro = {};

  Object.entries(hash).forEach(([tipoKey, nodo]) => {
    filtro[`${tipoKey}_id`] = nodo.id;
  });

  return filtro;
}

function buildParamsBusqueda() {
  const params = { tipo: tipo.value };
  if (filtroNombre.value) {
    params.filtro_nombre = filtroNombre.value;
  }
  if (usaEstructura.value) {
    const estructura = filtroEstructuraActual();
    if (Object.keys(estructura).length) {
      params.filtro_estructura = estructura;
    }
  }
  if (usaRoles.value && filtroRolIds.value.length) {
    params.filtro_rol_id = filtroRolIds.value;
  }
  if (usaGdc.value && filtroGdcIds.value.length) {
    params.filtro_gdc_id = filtroGdcIds.value;
  }
  return params;
}

function buildParamsConfirmacion() {
  const params = buildParamsBusqueda();
  if (usaDirecciones.value) {
    params.filtro_direccion_id = filtroDireccionIds.value;
  } else {
    params.seleccionado_id = seleccionados.value;
  }
  if (mostrarTiposDestinatario.value) {
    params.tipo_destinatario = tiposDestinatarioSeleccionados.value;
  }
  return params;
}

async function cargarTiposDestinatario() {
  if (!TIPOS_CON_TIPOS_DESTINATARIO.includes(tipo.value)) {
    tiposDestinatarioOpciones.value = [];
    tiposDestinatarioSeleccionados.value = [];
    return;
  }
  try {
    const opciones = await seleccionDestinatariosApi.tiposDestinatarios({
      tipo: tipo.value,
    });
    tiposDestinatarioOpciones.value = opciones;
    tiposDestinatarioSeleccionados.value = opciones.length
      ? [opciones[0].tipo]
      : [];
  } catch {
    tiposDestinatarioOpciones.value = [];
    tiposDestinatarioSeleccionados.value = [];
  }
}

async function cargarMetadatosFiltros() {
  if (usaRoles.value && !roles.value.length) {
    roles.value = await seleccionDestinatariosApi.listaRoles();
  }
  if (usaGdc.value && !gdcs.value.length) {
    gdcs.value = await seleccionDestinatariosApi.listaGruposContacto();
  }
  if (usaDirecciones.value && !direcciones.value.length) {
    direcciones.value = await seleccionDestinatariosApi.listaDirecciones();
  }
}

function resetSeleccion() {
  resultados.value = [];
  seleccionados.value = [];
  errorBusqueda.value = null;
}

async function buscarDestinatarios(seleccionarTodos = false) {
  if (usaGdc.value && !filtroGdcIds.value.length) {
    errorBusqueda.value = I18n.t('No_hay_registros');
    return;
  }
  buscando.value = true;
  errorBusqueda.value = null;
  try {
    const buscados = await seleccionDestinatariosApi.buscarDestinatarios(
      buildParamsBusqueda(),
    );
    resultados.value = buscados || [];
    if (seleccionarTodos) {
      seleccionResultados.seleccionarTodos();
    } else {
      seleccionResultados.deseleccionarTodos();
    }
    if (!resultados.value.length) {
      errorBusqueda.value = I18n.t('No_hay_registros');
    }
  } catch (err) {
    resultados.value = [];
    errorBusqueda.value = err.response?.data?.error?.message || I18n.t('No_hay_registros');
  } finally {
    buscando.value = false;
  }
}

async function cambiarTipo(nuevoTipo) {
  tipo.value = nuevoTipo;
  resetSeleccion();
  await cargarMetadatosFiltros();
  await cargarTiposDestinatario();
  if (tipo.value === 'destinatarios') {
    await buscarDestinatarios(false);
  }
}

async function validarYConfirmar() {
  const haySeleccion = usaDirecciones.value
    ? filtroDireccionIds.value.length > 0
    : seleccionados.value.length > 0;

  if (!haySeleccion) {
    await confirmar({
      title: 'Selección incompleta',
      text: 'La selección está incompleta, complete la selección, por favor',
      type: 'warning',
      showCancelButton: false,
      confirmButtonText: 'De acuerdo',
    });
    return;
  }

  if (
    mostrarTiposDestinatario.value
    && !tiposDestinatarioSeleccionados.value.length
  ) {
    await confirmar({
      title: 'Selección incompleta',
      text: 'La selección está incompleta, complete la selección, por favor',
      type: 'warning',
      showCancelButton: false,
      confirmButtonText: 'De acuerdo',
    });
    return;
  }

  enviando.value = true;
  try {
    const params = buildParamsConfirmacion();
    const destinatarios = await seleccionDestinatariosApi.destinatariosReales(params);
    emits('destinatarios-anadidos', destinatarios);
    emits('update:modelValue', false);
  } finally {
    enviando.value = false;
  }
}

async function inicializarModal() {
  inicializando.value = true;
  resetSeleccion();
  filtroNombre.value = '';
  filtroRolIds.value = [];
  filtroGdcIds.value = [];
  filtroDireccionIds.value = [];
  try {
    tiposBusqueda.value = await seleccionDestinatariosApi.tiposBusqueda();
    const tipoInicial = esMiembroCentro.value ? 'alumno' : 'destinatarios';
    await cambiarTipo(
      tiposBusqueda.value.find((t) => t.tipo === tipoInicial)?.tipo
        || tiposBusqueda.value[0]?.tipo,
    );
  } finally {
    inicializando.value = false;
  }
}

function cerrarModal() {
  emits('update:modelValue', false);
}

watch(
  () => props.modelValue,
  (visible) => {
    if (visible) {
      nextTick(() => inicializarModal());
    }
  },
);
</script>

<template>
  <ModalSimple
    id="modal-seleccion-destinatarios"
    :model-value="modelValue"
    :titulo="I18n.t('seleccion_destinatarios')"
    tamano="lg"
    :nivel-modal="2"
    class-modal="modal-seleccion-destinatarios modal-cifra-lg"
    :autofocus="false"
    @update:model-value="emits('update:modelValue', $event)"
  >
    <div id="selector-destinatarios" class="selector-destinatarios-vue">
      <div v-if="inicializando" class="text-center p-lg">
        <i class="fa fa-circle-notch fa-spin fa-2x"></i>
      </div>

      <template v-else>
        <div v-if="mostrarTipoSeleccion" class="text-center mb-3">
          <ul class="nav nav-pills nav-pills-fill d-inline-block">
            <li
              v-for="tipoBusqueda in tiposBusqueda"
              :key="tipoBusqueda.tipo"
              class="nav-item"
              :class="{ active: tipo === tipoBusqueda.tipo }"
            >
              <a
                href="#"
                class="nav-link"
                @click.prevent="cambiarTipo(tipoBusqueda.tipo)"
              >
                {{ tipoBusqueda.descripcion }}
              </a>
            </li>
          </ul>
        </div>

        <div v-if="tipo" id="filtros">
          <div v-if="usaEstructura" class="panel panel-default">
            <div class="panel-heading">
              <h4 class="panel-title">
                {{ I18n.t("filtro_estructura_tipo", { tipo: tipo }) }}
              </h4>
            </div>
            <div class="panel-body">
              <SelectorEstructura
                ref="selectorEstructura"
                :namespace-parametros="false"
                :nivel-modal="3"
                :tipos-seleccionables="[
                  'anno_lectivo',
                  'ensennanza',
                  'nivel',
                  'curso',
                  'modalidad',
                  'grupo',
                ]"
              />
            </div>
          </div>

          <div v-if="usaGdc" class="panel panel-default">
            <div class="panel-heading">
              <h4 class="panel-title">
                {{ I18n.t("seleccionar_gdc") }}
              </h4>
            </div>
            <div id="gdcs" class="h-300 table-responsive">
              <table class="table table-striped tabla-con-checkbox">
                <thead>
                  <tr>
                    <th class="col-min">
                      <BotonSeleccionarTodo
                        v-model="filtroGdcIds"
                        :valores="idsGdcsSeleccionables"
                      />
                    </th>
                    <th>{{ I18n.t("Nombre") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="gdc in gdcs"
                    :key="gdc.id"
                    class="clickable-row"
                    @click="seleccionGdc.toggleValor(gdc.id)"
                  >
                    <td @click.stop>
                      <InputCheckboxBoolean
                        :model-value="filtroGdcIds.includes(gdc.id)"
                        @update:model-value="(checked) => seleccionGdc.establecerValor(gdc.id, checked)"
                      />
                    </td>
                    <td>{{ gdc.nombre }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="usaRoles" class="panel panel-default">
            <div class="panel-heading">
              <h4 class="panel-title">
                {{ I18n.t("filtro_rol_title") }}
              </h4>
            </div>
            <div id="roles" class="h-300 table-responsive m-b-md">
              <table class="table table-striped tabla-con-checkbox">
                <thead>
                  <tr>
                    <th class="col-min"></th>
                    <th>{{ I18n.t("Nombre") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="rol in roles"
                    :key="rol.id"
                    class="clickable-row"
                    @click="seleccionRol.toggleValor(rol.id)"
                  >
                    <td @click.stop>
                      <InputCheckboxBoolean
                        :model-value="filtroRolIds.includes(rol.id)"
                        @update:model-value="(checked) => seleccionRol.establecerValor(rol.id, checked)"
                      />
                    </td>
                    <td>{{ rol.nombre }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="usaFiltroNombre" class="form-group">
            <InputBase v-model="filtroNombre" :placeholder="I18n.t('Nombre')" />
          </div>

          <div
            v-if="!usaDirecciones && tipo !== 'destinatarios'"
            class="form-group text-right"
          >
            <BotonGuardar
              id="btn-buscar-destinatararios"
              action="buscar"
              :submitting="buscando"
              @click="buscarDestinatarios(seleccionarTodosEnBusqueda)"
            />
          </div>
        </div>

        <div v-if="usaDirecciones" id="seleccion" class="row">
          <div class="col-xs-12">
            <div class="panel panel-default">
              <div class="panel-heading">
                <h4 class="panel-title">
                  {{ I18n.t("seleccionar_direccion_centro") }}
                </h4>
              </div>
              <div id="seleccionados" class="h-300 table-responsive">
                <table v-if="direcciones.length" class="table table-striped tabla-con-checkbox">
                  <thead>
                    <tr>
                      <th class="col-min"></th>
                      <th>{{ I18n.t("Nombre") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="direccion in direcciones"
                      :key="direccion.id"
                      class="clickable-row"
                      @click="seleccionDireccion.toggleValor(direccion.id)"
                    >
                      <td @click.stop>
                        <InputCheckboxBoolean
                          :model-value="filtroDireccionIds.includes(direccion.id)"
                          @update:model-value="(checked) => seleccionDireccion.establecerValor(direccion.id, checked)"
                        />
                      </td>
                      <td>{{ direccion.nombre }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="alert alert-info">
                  {{ I18n.t("No_hay_registros") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else-if="resultados.length || errorBusqueda || buscando"
          id="seleccion"
          class="row"
        >
          <div :class="mostrarTiposDestinatario ? 'col-xs-7' : 'col-xs-12'">
            <div class="panel panel-default">
              <div class="panel-heading">
                <h4 class="panel-title">
                  {{ tituloPanelSeleccion }}
                </h4>
              </div>
              <div>
                <div
                  v-if="buscando && !resultados.length"
                  class="text-center p-md"
                >
                  <i class="fa fa-circle-notch fa-spin"></i>
                  {{ I18n.t("cargando") }}
                </div>
                <div
                  v-else-if="errorBusqueda && !resultados.length"
                  class="text-danger p-3"
                >
                  <i class="fa fa-info-circle"></i> {{ errorBusqueda }}
                </div>
                <div
                  v-else
                  id="seleccionados"
                  class="h-300 table-responsive"
                  :class="{ 'buscando-resultados': buscando }"
                >
                  <table class="table table-striped tabla-con-checkbox">
                    <thead>
                      <tr>
                        <th class="col-min">
                          <BotonSeleccionarTodo
                            v-model="seleccionados"
                            :valores="idsSeleccionables"
                          />
                        </th>
                        <th v-if="tipo === 'destinatarios'"></th>
                        <th>{{ I18n.t("Nombre") }}</th>
                        <th v-if="tipo === 'alumno'">
                          {{ I18n.t("Grupo") }}
                        </th>
                        <th v-if="tipo === 'destinatarios'">
                          {{ I18n.t("Asignaturas") }}
                        </th>
                        <th v-if="tipo === 'destinatarios'">
                          {{ I18n.t("Grupo") }}
                        </th>
                        <th v-if="tipo === 'destinatarios'">
                          {{ I18n.t("Alumno") }}
                        </th>
                        <th v-if="tipo === 'asignatura'">
                          {{ I18n.t("abreviatura") }}
                        </th>
                        <th v-if="tipo === 'asignatura'">
                          {{ I18n.t("Etapa") }}
                        </th>
                        <th v-if="tipo === 'asignatura'">
                          {{ I18n.t("Curso") }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in resultados"
                        :key="`${item.id}-${item.alumno?.id || ''}`"
                        class="clickable-row"
                        @click="seleccionResultados.toggleValor(item.id)"
                      >
                        <td @click.stop>
                          <InputCheckboxBoolean
                            :model-value="seleccionados.includes(item.id)"
                            @update:model-value="(checked) => seleccionResultados.establecerValor(item.id, checked)"
                          />
                        </td>
                        <td v-if="tipo === 'destinatarios'">
                          <span
                            v-if="item.rol_academico"
                            class="label"
                            :class="item.rol_academico?.toLowerCase()"
                          >
                            {{ item.rol_academico }}
                          </span>
                        </td>
                        <td>
                          <img
                            v-if="item.foto_url"
                            :src="item.foto_url"
                            class="usuario-thumbnail usuario-thumbnail-sm
                                   img-circle img-thumbnail me-2"
                          >
                          {{ nombreResultado(item) }}
                        </td>
                        <td v-if="tipo === 'alumno'">
                          {{ item.grupo?.etiqueta }}
                        </td>
                        <td v-if="tipo === 'destinatarios'">
                          <ul
                            v-if="item.asignaturas?.length"
                            class="unstyled p-n"
                          >
                            <li v-for="asig in item.asignaturas" :key="asig.id">
                              {{ asig.nombre }}
                            </li>
                          </ul>
                        </td>
                        <td v-if="tipo === 'destinatarios'">
                          {{ item.grupo?.etiqueta }}
                        </td>
                        <td v-if="tipo === 'destinatarios'">
                          {{
                            item.alumno?.nombre_completo || item.alumno?.nombre
                          }}
                        </td>
                        <td v-if="tipo === 'asignatura'">
                          {{ item.abreviatura }}
                        </td>
                        <td v-if="tipo === 'asignatura'">
                          {{
                            item.curso?.ensennanza?.nombre ||
                              item.ensennanza?.nombre
                          }}
                        </td>
                        <td v-if="tipo === 'asignatura'">
                          {{ item.curso?.nombre }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div v-if="mostrarTiposDestinatario" class="col-xs-5">
            <div id="tipos-destinatarios" class="panel panel-default">
              <div class="panel-heading">
                <h4 class="panel-title">
                  {{ I18n.t("destinatarios_title") }}
                </h4>
              </div>
              <div class="h-300 table-responsive">
                <table class="table table-striped tabla-con-checkbox">
                  <thead>
                    <tr>
                      <th class="col-min">
                        <BotonSeleccionarTodo
                          v-model="tiposDestinatarioSeleccionados"
                          :valores="tiposDestinatarioValores"
                        />
                      </th>
                      <th>{{ I18n.t("Nombre") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="opcion in tiposDestinatarioOpciones"
                      :key="opcion.tipo"
                      class="clickable-row"
                      @click="seleccionTipoDestinatario.toggleValor(opcion.tipo)"
                    >
                      <td @click.stop>
                        <InputCheckboxBoolean
                          :model-value="tiposDestinatarioSeleccionados.includes(opcion.tipo)"
                          @update:model-value="
                            (checked) => seleccionTipoDestinatario.establecerValor(opcion.tipo, checked)
                          "
                        />
                      </td>
                      <td>{{ opcion.descripcion }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <template v-if="!inicializando" #modalFooter>
      <button
        type="button"
        class="btn btn-default"
        data-dismiss="modal"
        @click="cerrarModal"
      >
        {{ I18n.t('Cancelar') }}
      </button>
      <BotonGuardar
        id="btn-submit-seleccion"
        :submitting="enviando"
        action="anadir"
        @click="validarYConfirmar"
      />
    </template>
  </ModalSimple>
</template>

<style lang="scss" scoped>
.selector-destinatarios-vue {
  .h-300 {
    max-height: 300px;
    overflow-y: auto;
  }

  .buscando-resultados {
    opacity: 0.5;
    pointer-events: none;
  }

  .tabla-con-checkbox {
    th, td {
      vertical-align: middle;
    }

    th:first-child,
    td:first-child {
      text-align: center;
      vertical-align: middle;
    }
  }

  .clickable-row {
    cursor: pointer;
  }
}
</style>
